import {useEffect, useRef, useState} from "react";
import {Stomp} from "@stomp/stompjs";
import SockJS from "sockjs-client";
import ConfigUtil from "../../../../util/ConfigUtil";
import FetchUtil from "../../../../util/FetchUtil";
import WebPushUtil from "../../../../util/WebPushUtil";

/**
 * Lädt die öffentlichen Turnierdaten und hält sie per Websocket aktuell.
 * Ist die Seite nicht sichtbar (Tab im Hintergrund, Handy gesperrt), wird die Verbindung getrennt
 * und beim Zurückkehren neu aufgebaut. Dabei werden die Daten neu geladen, damit nichts verpasst wird.
 */
export default function useTournamentLive(tournamentId) {

    const [tournamentWrapper, setTournamentWrapper] = useState(undefined);
    const [liveGames, setLiveGames] = useState([]);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [verbunden, setVerbunden] = useState(false);
    const [supportsWebPush, setSupportsWebPush] = useState(undefined);
    const [pushSpielerIds, setPushSpielerIds] = useState([]);

    const stompClientRef = useRef(undefined);
    // zählt die Live-Nachrichten per Websocket, damit eine langsamere REST-Antwort keine neueren Daten überschreibt
    const anzahlLiveNachrichten = useRef(0);

    useEffect(() => {
        ladeDaten(tournamentId);
        ladeLiveGames(tournamentId);
        verbinden();

        function handleVisibilityChange() {
            if (document.hidden) {
                trennen();
            } else {
                ladeDaten(tournamentId);
                ladeLiveGames(tournamentId);
                verbinden();
            }
        }
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            trennen();
        };
    }, [tournamentId]);

    const verbinden = () => {
        if (stompClientRef.current !== undefined || tournamentId === null || tournamentId === undefined) {
            return;
        }
        const stompClient = Stomp.over(() => new SockJS(ConfigUtil.getConfig().websocketUrl));
        stompClient.reconnect_delay = 10000;
        stompClient.debug = () => {};
        stompClient.connect({},
            () => {
                setVerbunden(true);
                stompClient.subscribe("/topic/public/tournament/publicview/" + tournamentId, (payload) => {
                    setTournamentWrapper(JSON.parse(payload.body));
                });
                stompClient.subscribe("/topic/public/tournament/game/infos/" + tournamentId, (payload) => {
                    anzahlLiveNachrichten.current++;
                    setLiveGames(JSON.parse(payload.body).gameShortInfos);
                });
            },
            (error) => {
                console.log("Websocket - connect error:", error);
                setVerbunden(false);
            },
            () => {
                setVerbunden(false);
            });
        stompClientRef.current = stompClient;
    }

    const trennen = () => {
        if (stompClientRef.current !== undefined) {
            stompClientRef.current.disconnect(() => {});
            stompClientRef.current = undefined;
        }
        setVerbunden(false);
    }

    // Die erste Websocket-Nachricht nach dem Subscribe kann verloren gehen (der Broker hat das Abo noch nicht registriert),
    // daher werden die Live-Spiele zum Start zusätzlich per REST geladen.
    const ladeLiveGames = (id) => {
        if (id === null || id === undefined) {
            return;
        }
        const nachrichtenVorher = anzahlLiveNachrichten.current;
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/tournament/gameshortinfos/" + id,
            dto => {
                if (anzahlLiveNachrichten.current === nachrichtenVorher) {
                    setLiveGames(dto.gameShortInfos);
                }
            },
            responseNotOk => {
                console.log("Fehler beim Laden der Live-Spiele: " + responseNotOk.message);
            },
            error => {
                console.log("Fehler beim Laden der Live-Spiele: " + error);
            });
    }

    const ladeDaten = async (id) => {
        setErrorMessage(undefined);
        if (id === null || id === undefined) {
            return;
        }
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/tournament/" + id,
            json => {
                setTournamentWrapper(json);
            },
            responseNotOk => {
                setErrorMessage("Fehler: " + responseNotOk.message);
            },
            error => {
                setErrorMessage("Fehler: " + error.message);
            });

        const webPushMoeglich = await WebPushUtil.check();
        setSupportsWebPush(webPushMoeglich);
        if (webPushMoeglich) {
            updateSubscriptions();
        }
    }

    const updateSubscriptions = () => {
        WebPushUtil.subscriptionsLaden(tournamentId,
            ok => {
                setPushSpielerIds(ok.spielerIds)
            });
    }

    return {
        tournamentWrapper, liveGames, errorMessage, setErrorMessage, verbunden,
        supportsWebPush, pushSpielerIds, updateSubscriptions,
        ladeDaten,
    };
}
